import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, Chip, CircularProgress,
} from '@mui/material';
import { fieldSx, labelSx } from '../styles/formStyles';

const API = process.env.REACT_APP_API_URL || '';

const ESTADOS = [
  { value: '',            label: 'Todas las acciones' },
  { value: 'pendiente',   label: 'Pendiente' },
  { value: 'confirmado',  label: 'Confirmado' },
  { value: 'en_recepcion', label: 'En recepción' },
  { value: 'demorado',    label: 'Demorado' },
  { value: 'atendido',    label: 'Atendido' },
  { value: 'cancelado',   label: 'Cancelado' },
];

const COLORES_ESTADO = {
  pendiente: '#90a4ae',
  confirmado: '#42a5f5',
  en_recepcion: '#66bb6a',
  demorado: '#ffa726',
  atendido: '#26a69a',
  cancelado: '#ef5350',
};

function EstadoChip({ valor }) {
  if (!valor) return <Typography sx={{ color: '#546e7a', fontSize: 12 }}>—</Typography>;
  return (
    <Chip
      label={valor.replace('_', ' ')}
      size="small"
      sx={{
        bgcolor: `${COLORES_ESTADO[valor] || '#90a4ae'}22`,
        color: COLORES_ESTADO[valor] || '#90a4ae',
        border: `1px solid ${COLORES_ESTADO[valor] || '#90a4ae'}55`,
        fontSize: 11,
        height: 22,
      }}
    />
  );
}

function formatFechaHora(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

function formatFechaTurno(fecha, hora) {
  if (!fecha) return '—';
  // El backend puede mandar esto como fecha "pelada" (YYYY-MM-DD) o como ISO completo con hora
  // y zona (columna DATE serializada por Node). Hay que pelarla primero -- si no, pegarle
  // 'T00:00:00' a un string que ya tiene su propia hora arma un texto inválido (Invalid Date).
  const soloFecha = String(fecha).split('T')[0];
  const d = new Date(soloFecha + 'T00:00:00');
  const f = d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  return hora ? `${f} ${String(hora).slice(0, 5)}` : f;
}

const hoy = new Date().toISOString().slice(0, 10);
const hace30 = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);

export default function AuditoriaTurnos() {
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [filtros, setFiltros] = useState({
    desde: hace30,
    hasta: hoy,
    usuario: '',
    accion: '',
  });

  const f = field => e => setFiltros(prev => ({ ...prev, [field]: e.target.value }));

  const cargar = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      const params = {};
      if (filtros.desde) params.desde = filtros.desde;
      if (filtros.hasta) params.hasta = filtros.hasta;
      if (filtros.usuario.trim()) params.usuario = filtros.usuario.trim();
      if (filtros.accion) params.accion = filtros.accion;
      const res = await axios.get(`${API}/api/auditoria/turnos`, { params });
      setFilas(res.data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al cargar la auditoría.');
    } finally {
      setCargando(false);
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Box sx={{ p: 3, maxWidth: 1400 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#e0e0e0', mb: 3 }}>
        Auditoría de Turnos
      </Typography>

      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, alignItems: 'flex-end' }}>
        <Box>
          <Typography sx={labelSx}>Desde</Typography>
          <TextField type="date" value={filtros.desde} onChange={f('desde')}
            inputProps={{ style: { colorScheme: 'dark' } }} sx={{ ...fieldSx, width: 160 }} />
        </Box>
        <Box>
          <Typography sx={labelSx}>Hasta</Typography>
          <TextField type="date" value={filtros.hasta} onChange={f('hasta')}
            inputProps={{ style: { colorScheme: 'dark' } }} sx={{ ...fieldSx, width: 160 }} />
        </Box>
        <Box>
          <Typography sx={labelSx}>Usuario</Typography>
          <TextField value={filtros.usuario} onChange={f('usuario')}
            placeholder="Buscar por nombre..." sx={{ ...fieldSx, width: 200 }} />
        </Box>
        <Box>
          <Typography sx={labelSx}>Acción</Typography>
          <FormControl sx={{ ...fieldSx, width: 190 }}>
            <Select value={filtros.accion} onChange={f('accion')}
              displayEmpty sx={{ color: '#e0e0e0' }}>
              {ESTADOS.map(e => (
                <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && (
        <Typography sx={{ color: '#ef5350', mb: 2, fontSize: 14 }}>{error}</Typography>
      )}

      {cargando ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} sx={{ color: 'var(--c-acento)' }} />
        </Box>
      ) : (
        <>
          <Typography sx={{ fontSize: 12, color: '#546e7a', mb: 1 }}>
            {filas.length} registro{filas.length !== 1 ? 's' : ''}
            {filas.length === 500 ? ' (límite 500 — ajustá los filtros para ver más)' : ''}
          </Typography>
          <TableContainer component={Paper} sx={{ bgcolor: '#16213e', border: '1px solid #1e3a5f' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { color: '#546e7a', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '1px solid #1e3a5f', py: 1 } }}>
                  <TableCell>Fecha/hora evento</TableCell>
                  <TableCell>Turno</TableCell>
                  <TableCell>Paciente</TableCell>
                  <TableCell>Médico</TableCell>
                  <TableCell>Estado anterior</TableCell>
                  <TableCell>Estado nuevo</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Rol</TableCell>
                  <TableCell>Motivo</TableCell>
                  <TableCell>Origen</TableCell>
                  <TableCell>IP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 4, color: '#546e7a', border: 'none' }}>
                      No hay registros para los filtros seleccionados.
                    </TableCell>
                  </TableRow>
                ) : filas.map(row => (
                  <TableRow key={row.id} sx={{
                    '&:hover': { bgcolor: 'rgba(144,202,249,0.04)' },
                    '& td': { borderBottom: '1px solid #1e3a5f', py: 1, fontSize: 13 }
                  }}>
                    <TableCell sx={{ color: '#90a4ae', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {formatFechaHora(row.created_at)}
                    </TableCell>
                    <TableCell sx={{ color: '#b0bec5', whiteSpace: 'nowrap' }}>
                      #{row.turno_id} — {formatFechaTurno(row.turno_fecha, row.turno_hora)}
                    </TableCell>
                    <TableCell sx={{ color: '#e0e0e0' }}>
                      {row.paciente_nombre || <span style={{ color: '#546e7a' }}>—</span>}
                    </TableCell>
                    <TableCell sx={{ color: '#b0bec5' }}>
                      {row.medico_nombre || <span style={{ color: '#546e7a' }}>—</span>}
                    </TableCell>
                    <TableCell><EstadoChip valor={row.estado_anterior} /></TableCell>
                    <TableCell><EstadoChip valor={row.estado_nuevo} /></TableCell>
                    <TableCell sx={{ color: '#e0e0e0' }}>{row.usuario_nombre}</TableCell>
                    <TableCell>
                      <Chip label={row.usuario_rol} size="small"
                        sx={{ fontSize: 10, height: 18, bgcolor: 'rgba(144,202,249,0.1)', color: '#90caf9', border: '1px solid rgba(144,202,249,0.2)' }} />
                    </TableCell>
                    <TableCell sx={{ color: '#90a4ae', maxWidth: 200 }}>
                      {row.motivo || <span style={{ color: '#37474f' }}>—</span>}
                    </TableCell>
                    <TableCell sx={{ color: '#b0bec5', fontSize: 12 }}>
                      {row.origen || <span style={{ color: '#37474f' }}>—</span>}
                    </TableCell>
                    <TableCell sx={{ color: '#546e7a', fontSize: 11, fontFamily: 'monospace' }}>
                      {row.ip_cliente || <span style={{ color: '#37474f' }}>—</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
